import React, { ChangeEvent, FC, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  AnimalFragment,
  useGetAnimalsPageLazyQuery,
  useGroupsInfoQuery,
  useFieldNamesQuery,
  GetAnimalsPageQueryVariables,
  useAnimalsExportAsyncMutation,
  AnimalSorting,
  IdentifierType,
  AnimalStatus,
  UploadCsvDateHintEnumType,
  AnimalExportColumnInput,
} from "generated/graphql";

import cn from "classnames";
import { CellProps, Column } from "react-table";
import { DateTime } from "luxon";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";

import { TableOptionsContext } from "config/tableOptionsProvider";
import { getDaysOnFarm, getFieldName, getMonthsAge } from "helpers/myLivestock";
import { getBUFromLocalStorage, getTableSettingsFromLocalStorage } from "helpers/storage";
import { getFormattedNumber, removeNothings } from "helpers/general";
import {
  areAllRowsSelected,
  reorderColumns,
  createColumnObject,
  sortRuleToAnimalSorting,
  getAnimalExportColumn,
  getBooleanFilter,
} from "./helpers";
import {
  useFormatDate,
  useGetCurrentBusinessUnit,
  useStateSpecies,
  useGetAnimalIdPreferences,
  useHasFeature,
  useGetBreeds,
} from "hooks";

import TableActionColumn from "pages/MyLivestock/Animals/TableActionColumn";
import { AnimalActions } from "animals/AnimalActions";
import { AnimalPregnancyLabel, AnimalsPerformancePill, AnimalsSexClassLabel } from "components/MyLivestock";
import { BaseInput } from "components/Common/Field/BaseInput";
import { Button as TwButton } from "twComponents/Button";
import {
  Button,
  FieldDateRange,
  FieldSelect,
  Flex,
  Icon,
  InputCheckbox,
  LoadingOverlay,
  Pill,
  Spacer,
  Table,
  TableHeaderCaptions,
} from "components";
import { renderPedigreeStatus, renderStatus } from "components/MyLivestock/Animals/AnimalsTable";
import { SelectedAnimalsTable } from "animals/SelectedAnimalsList";
import { TABLE_IDS } from "constants/Interface";
import { Checkbox, getActionsColumn, TablePaginationTarget } from "components/Common/Table";
import { ExportAnimalsButton } from "components/MyLivestock/Animals/AnimalFiltersBlock/ExportAnimalsButton";
import { PregnancyStatus, Props, Sex } from "./types";
import paginationStyles from "components/Common/Table/TablePagination/TablePagination.module.scss";
import styles from "./AnimalsList.module.scss";
import { Tag } from "tags/components/Tag";
import { useTags } from "tags/hooks/useTagsHook";
import { InputSelectOption } from "components/Common/Field/Select";
import { ColumnOptionsModal } from "components/Common/Table/ColumnOptionsModal";
import { COLUMN_IDS, DEFAULT_COLUMN_IDS, DEFAULT_COLUMN_IDS_US, DEFAULT_COLUMN_IDS_US_ARCHIVE } from "./constants";
import { useDispatch } from "react-redux";
import { setMenuState, setTaskId } from "state/asyncJobSlice";
import { useSelector } from "react-redux";
import { RootState } from "state/store";
import { updateFilters, toggleFilter, resetFilters } from "state/animalFiltersSlice";
import { capitaliseFirstLetter, capitaliseFirstLetterOfEachWord } from "helpers/translations/src/format";
import { convertSnakeToCamelCase, identifierNameLookup } from "components/Settings/IdPreferencesForm/helpers";
import { useLocale } from "helpers/translations/src/useLocale";
import { getLoadingStateRows } from "utils/misc";
import { useServerPagination } from "twComponents/PaginationControl/helpers";
import { PaginationControl } from "twComponents/PaginationControl";
import { AnimalPedigreeLabel } from "components/MyLivestock/Animals/AnimalPedigreeLabel";
import { EmptyStatesMyCattleTab } from "components/EmptyStates/MyCattleTab";
import { EmptyStatesNoResults } from "components/EmptyStates/NoResults";
import { AnimalsBreedPercentages } from "components/MyLivestock/Animals/AnimalsBreedPercentages";

export const AnimalsList: FC<Props> = ({
  tableId,
  fromPage,
  isArchive = false,
  fieldId,
  groupId,
  tagId,
  onPressEditAnimal,
  showSelectColumn,
  showAnimalActionButtons,
  showIndividualAnimalActionButtons = true,
  excludedFilters,
  tableFilterIdentifier,
  rowStyles,
  animalIds,
  animalStatus,
  resetSelectedAnimalsOnUnmount = true,
  animalTypeIds,
}) => {
  const businessUnitId = getBUFromLocalStorage();
  const history = useHistory();
  const { getTableOptions, setTableOptions } = useContext(TableOptionsContext);
  const [sortBy, setSortBy] = React.useState<AnimalSorting>(AnimalSorting.UpdatedAtDesc);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSelectedAnimalsView, setIsSelectedAnimalsView] = useState(false);
  const { getFormatDate } = useFormatDate();
  const { weightUnits, countryIsoCode } = useGetCurrentBusinessUnit();
  const countryCode = { us: countryIsoCode === "us", gb: countryIsoCode === "gb", au: countryIsoCode === "au" };
  const { activeSpecies } = useStateSpecies();
  const activeSpeciesTypeId = activeSpecies?.id ? parseInt(activeSpecies?.id) : undefined;
  const { breedOptions } = useGetBreeds();
  const { terms } = useLocale();
  const [columnOptionsModalOpen, setColumnOptionsModalOpen] = useState(false);
  const currentLocalStorageSettings = getTableSettingsFromLocalStorage(tableId) || [];
  const currentLocalStorageColumns = currentLocalStorageSettings?.columns || [];
  const { activeIdPreferences, primaryId, secondaryId } = useGetAnimalIdPreferences();
  const primaryIdCamelCase = convertSnakeToCamelCase(primaryId);
  const secondaryIdCamelCase = convertSnakeToCamelCase(secondaryId);
  const [exportCsv] = useAnimalsExportAsyncMutation();

  const dispatch = useDispatch();
  const taskId = useSelector((state: RootState) => state.asyncJob.taskId);

  const useEpds = useHasFeature("EPDS");

  const { data: fieldsData } = useFieldNamesQuery({
    variables: {
      businessUnitId,
    },
  });

  const fieldOptions: InputSelectOption[] = React.useMemo(
    () =>
      (fieldsData?.fields || [])
        .map((field) => {
          return {
            value: field?.id,
            key: field?.id,
            label: getFieldName({ fieldName: field.name, unitName: field.location?.unitName }),
          };
        })
        .sort((fieldA, fieldB) => fieldA.label.localeCompare(fieldB.label)),
    [fieldsData?.fields],
  );

  const { animalTags } = useTags();

  const animalTagOptions: InputSelectOption[] = React.useMemo(
    () =>
      animalTags.map((item) => {
        return { value: item?.node?.id || "", id: item?.node?.id, label: item?.node?.name, key: item?.node?.id || "" };
      }),
    [animalTags],
  );

  const { data: groupsData } = useGroupsInfoQuery({
    variables: {
      businessUnitId,
      animalTypeIds: fromPage === "field" ? undefined : activeSpeciesTypeId,
    },
  });

  const groups = removeNothings(groupsData?.groupsExtended?.groups ?? []);
  const numberOfGroups = groupsData?.groupsExtended?.groupsCount;

  const { filters, showFilters } = useSelector((state: RootState) => state.animalFilters[tableFilterIdentifier]);

  const tableOptions = getTableOptions<AnimalFragment>(tableId);
  const sortByValue = tableOptions?.sortBy;
  const selectedRowIds = tableOptions?.selectedRowsIds;

  const [getAnimals, { data, loading }] = useGetAnimalsPageLazyQuery({
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const totalNumberOfAnimals = data?.animals?.count ?? 0;

  const paginationProps = useServerPagination({
    totalNumberOfItems: totalNumberOfAnimals,
    pageInfo: data?.animals?.pageInfo,
    loading,
    tableId,
  });

  const { pagination, pageSize, setPageNumber } = paginationProps;

  useEffect(() => {
    const variables: GetAnimalsPageQueryVariables = {
      search: searchTerm,
      searchIds: [
        IdentifierType.EId,
        primaryId ? (primaryId as unknown as IdentifierType) : IdentifierType.VisualId,
        secondaryId ? (secondaryId as unknown as IdentifierType) : IdentifierType.VisualId,
      ],
      breed: filters.breed || undefined,
      isMale: getBooleanFilter(filters.sex, Sex.MALE, Sex.FEMALE),
      isPregnant: getBooleanFilter(filters.pregnancyStatus, PregnancyStatus.PREGNANT, PregnancyStatus.NOTPREGNANT),
      minDob: filters.dateOfBirth.from || undefined,
      maxDob: filters.dateOfBirth.to || undefined,
      minDof: filters.dateOnFarm.from || undefined,
      maxDof: filters.dateOnFarm.to || undefined,
      field: fieldId || filters.fieldId || undefined,
      group: groupId || filters.groupId || undefined,
      businessUnitId: businessUnitId,
      last: pagination.last,
      before: pagination.before,
      first: pagination.first,
      after: pagination.after,
      orderBy: sortBy ? [sortBy] : undefined,
      animalTags: tagId ? [tagId] : filters.tagIds.length > 0 ? filters.tagIds : undefined,
      animalTypeIds: animalTypeIds ? animalTypeIds : fromPage === "field" ? undefined : activeSpeciesTypeId,
      animalIds,
      status: animalStatus ? animalStatus : !isArchive ? AnimalStatus.Active : AnimalStatus.Inactive,
    };
    getAnimals({
      variables,
      notifyOnNetworkStatusChange: true,
    });
  }, [
    animalStatus,
    getAnimals,
    sortBy,
    pageSize,
    businessUnitId,
    filters.fieldId,
    filters.groupId,
    filters.sex,
    filters.breed,
    filters.pregnancyStatus,
    filters.dateOfBirth.from,
    filters.dateOfBirth.to,
    filters.dateOnFarm.from,
    filters.dateOnFarm.to,
    searchTerm,
    pagination,
    filters.tagIds,
    fieldId,
    groupId,
    fromPage,
    activeSpeciesTypeId,
    primaryId,
    secondaryId,
    tagId,
    isArchive,
    animalIds,
    animalTypeIds,
  ]);

  const animals = useMemo(
    () =>
      removeNothings(
        data?.animals?.edges.map((animal) => {
          return animal?.node;
        }) ?? [],
      ),
    [data],
  );

  useEffect(() => {
    if (sortByValue) {
      setPageNumber(1);
      setSortBy(sortRuleToAnimalSorting(sortByValue[0]));
    }
  }, [setPageNumber, sortByValue]);

  const selectedAnimalIds = useMemo(() => (selectedRowIds ? [...selectedRowIds] : []), [selectedRowIds]);

  const onCellClick = useCallback(
    (animal: AnimalFragment) => {
      history.push(`/system/${fromPage || "livestock"}/animal/${animal.id}`);
    },
    [history, fromPage],
  );

  //@ts-expect-error
  const columns: Column<AnimalFragment>[] = useMemo(
    () => [
      {
        id: COLUMN_IDS.SELECTION,
        Header: function SelectAllHeader({ data }: { data: AnimalFragment[] }) {
          const animalIds: Array<string> = data.map((a) => a.id);
          const selectedRowsIds = tableOptions?.selectedRowsIds;
          const checked = areAllRowsSelected(animalIds, selectedRowsIds ?? undefined);

          const updatedRows = new Set(
            Array.from(selectedRowsIds ?? []).filter((rowId) => {
              return !animalIds.includes(rowId);
            }),
          );

          const handleCheckboxChange = (): void => {
            if (checked) {
              setTableOptions({
                id: tableId,
                selectedRowsIds: updatedRows,
              });
            } else {
              const ids = new Set<string>(selectedRowsIds);

              data.forEach((item) => {
                ids.add(item["id"]);
              });

              setTableOptions({
                id: tableId,
                selectedRowsIds: ids,
              });
            }
          };
          return showSelectColumn ? <InputCheckbox checked={checked} onChange={handleCheckboxChange} /> : null;
        },
        Cell: function SelectColumn(
          props: React.PropsWithChildren<CellProps<AnimalFragment, string | null | undefined>>,
        ): React.ReactNode {
          const id = props.cell.row.original.id;

          return showSelectColumn ? <Checkbox tableId={tableId} id={id} /> : null;
        },
        sticky: "left",
        minWidth: showSelectColumn ? 40 : 0,
        width: showSelectColumn ? 40 : 0,
      },
      {
        id: COLUMN_IDS.ANIMAL_ID,
        Header: capitaliseFirstLetterOfEachWord(terms.passportNumber),
        accessor: "passportNumber",
        onCellClick,
        minWidth: 170,
        width: 170,
      },
      {
        id: COLUMN_IDS.E_ID,
        Header: "EID",
        accessor: "eId",
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.E_ID] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.VISUAL_ID,
        Header: "VID",
        accessor: "visualId",
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.VISUAL_ID] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.BREEDR_ID,
        Header: "Breedr ID",
        onCellClick,
        accessor: "breedrId",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.BREEDR_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.NAME,
        Header: "Name",
        onCellClick,
        accessor: "name",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.NAME] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.BRUCELLOSIS_ID,
        Header: "Brucellosis ID",
        onCellClick,
        accessor: "brucellosisId",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.BRUCELLOSIS_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.TRICH_ID,
        Header: "Trich ID",
        onCellClick,
        accessor: "trichId",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.TRICH_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.TSU_BARCODE,
        Header: "TSU Barcode",
        onCellClick,
        accessor: "tsuBarcode",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.TSU_BARCODE] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.HERDDOGG_ID,
        Header: "HerdDogg ID",
        onCellClick,
        accessor: "herdDoggId",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.HERDDOGG_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.PEDIGREE_ID,
        Header: capitaliseFirstLetterOfEachWord(terms.pedigreeId),
        onCellClick,
        accessor: "pedigreeId",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.PEDIGREE_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.TATTOO_ID,
        Header: "Tattoo ID",
        onCellClick,
        accessor: "tattoo",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.TATTOO_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.UHF_ID,
        Header: "UHF ID",
        onCellClick,
        accessor: "uhfId",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.UHF_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.ALTERNATIVE_ID,
        Header: "Alternative ID",
        onCellClick,
        accessor: "alternativeId",
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.ALTERNATIVE_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DATE_OF_BIRTH,
        Header: "DOB",
        accessor: "dob",
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DATE_OF_BIRTH] ? getFormatDate(row.values[COLUMN_IDS.DATE_OF_BIRTH]) : "\u2014",
        onCellClick,
        minWidth: 120,
        width: 120,
      },
      {
        id: COLUMN_IDS.AGE,
        Header: <TableHeaderCaptions title="Age" subtitle="Months" />,
        accessor: ({ dateLeftFarm, deadAt, dob }): number | null => {
          // if dead, calculate age at death
          if (deadAt) return getMonthsAge(dob, deadAt);

          // if off farm, calculate age at move off farm
          if (dateLeftFarm) return getMonthsAge(dob, dateLeftFarm);

          // otherwise just calculate current age
          return getMonthsAge(dob);
        },
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.AGE] || "\u2014",
        minWidth: 100,
        width: 100,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.SEX_CLASSIFICATION,
        Header: <TableHeaderCaptions title="Sex" subtitle="Classification" />,
        accessor: ({ isMale, sexClassification }): string => sexClassification?.slug || (isMale ? "M" : "F"),
        onCellClick,
        Cell: function SexCell({ row }): React.ReactNode {
          const { id, isCastrated, isMale, sexClassification } = row?.original;

          return (
            <AnimalsSexClassLabel
              id={`animal_sex_label__${id}`}
              isCastrated={isCastrated}
              isMale={isMale}
              label={sexClassification?.title || null}
            />
          );
        },
        minWidth: 130,
        width: 100,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.SEX,
        Header: "Sex",
        accessor: ({ isMale }): string => (isMale ? "M" : "F"),
        onCellClick,
        Cell: function SexCell({ row }): React.ReactNode {
          const { id, isCastrated, isMale } = row?.original;

          return (
            <AnimalsSexClassLabel
              id={`animal_sex_label__${id}`}
              isCastrated={isCastrated}
              isMale={isMale}
              label={isMale ? "M" : "F"}
            />
          );
        },
        minWidth: 100,
        width: 100,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.BREED,
        Header: "Breed",
        accessor: "breedPercentages",
        onCellClick,
        Cell: function BreedsCell({ row }) {
          const { breedPercentages } = row?.original;
          return <AnimalsBreedPercentages breedPercentages={breedPercentages} orientation="col" showFullName />;
        },
        minWidth: 150,
        width: 200,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.BREED_CODE,
        Header: "Breed Code",
        accessor: "breedPercentages",
        onCellClick,
        Cell: function BreedsCell({ row }) {
          const { breedPercentages } = row?.original;
          return <AnimalsBreedPercentages breedPercentages={breedPercentages} orientation="row" showFullName={false} />;
        },
        minWidth: 150,
        width: 200,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.TAGS,
        Header: "Tags",
        accessor: "tags",
        onCellClick,
        Cell: function TagsCell({ row }) {
          const { tags } = row?.original;

          return tags.map((tag) => {
            const t = {
              tagId: tag.tagId,
              name: tag.name,
              color: tag.color || "grey",
              ownership: tag?.schema?.ownership,
            };
            return <Tag key={tag.tagId} tag={t} />;
          });
        },
        minWidth: 100,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.GROUP,
        Header: "Group",
        accessor: ({ group }): string | null => group?.name || null,
        onCellClick,
        Cell: ({ row }): React.ReactElement => row.values[COLUMN_IDS.GROUP] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.FIELD,
        Header: "Location",
        accessor: ({ field }): string | null =>
          field ? getFieldName({ fieldName: field.name, unitName: field.location?.unitName }) : null,
        onCellClick,
        Cell: ({ row }): React.ReactElement => row.values[COLUMN_IDS.FIELD] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.PREVIOUS_KEEPER,
        Header: capitaliseFirstLetterOfEachWord(terms.previousKeeper),
        onCellClick,
        accessor: ({ previousKeeper }): string | null => previousKeeper?.name || null,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.PREVIOUS_KEEPER] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.UPDATED_AT,
        Header: "Last Updated",
        onCellClick,
        accessor: ({ updatedAt }): string | null => (updatedAt ? getFormatDate(updatedAt) : "\u2014"),
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.UPDATED_AT] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.DATE_ON_FARM,
        Header: `Date On ${capitaliseFirstLetter(terms.farm)}`,
        accessor: "dateMovedToFarm",
        onCellClick,
        Cell: ({ value }): React.ReactNode => (value ? getFormatDate(value) : "\u2014"),
        minWidth: 120,
        width: 120,
      },
      {
        id: COLUMN_IDS.DAYS_ON_FARM,
        Header: `Days On ${capitaliseFirstLetter(terms.farm)}`,
        accessor: (row): number | null =>
          row?.dateMovedToFarm
            ? getDaysOnFarm({
                dateMovedToFarm: row.dateMovedToFarm,
                dateLeftFarm: row?.dateLeftFarm,
                deadAt: row?.deadAt,
              })
            : null,
        onCellClick,
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DAYS_ON_FARM] ? `${row.values[COLUMN_IDS.DAYS_ON_FARM]} days` : "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DATE_LEFT_FARM,
        Header: `Date Left ${capitaliseFirstLetter(terms.farm)}`,
        onCellClick,
        accessor: "dateLeftFarm",
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DATE_LEFT_FARM] ? getFormatDate(row.values[COLUMN_IDS.DATE_LEFT_FARM]) : "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.DEAD_AT,
        Header: "Dead At Date",
        onCellClick,
        accessor: "deadAt",
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DEAD_AT] ? getFormatDate(row.values[COLUMN_IDS.DEAD_AT]) : "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.CURRENT_WEIGHT,
        Header: <TableHeaderCaptions title="Current Weight" subtitle="Estimated" />,
        accessor: ({ currentWeight }): number | null => (currentWeight ? +currentWeight?.toFixed(1) : null),
        onCellClick,
        Cell: function CurrentWeightCell({ row }): React.ReactNode {
          return !!row.values[COLUMN_IDS.CURRENT_WEIGHT] ? (
            <AnimalsPerformancePill
              label={`${row.values[COLUMN_IDS.CURRENT_WEIGHT]} ${weightUnits}`}
              performanceCategory={row?.original?.performanceCategory}
            />
          ) : (
            "\u2014"
          );
        },
        minWidth: 160,
        width: 160,
      },
      {
        id: COLUMN_IDS.LAST_WEIGHT,
        Header: "Last Weight",
        accessor: ({ lastWeight }): number | null => lastWeight?.value || null,
        Cell: function LastWeightCell({ row }): React.ReactNode {
          return !!row.values[COLUMN_IDS.LAST_WEIGHT] ? (
            <Pill caption={`${getFormattedNumber(row.values[COLUMN_IDS.LAST_WEIGHT])} ${weightUnits}`} colour="grey" />
          ) : (
            "\u2014"
          );
        },
        minWidth: 120,
        width: 120,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DAYS_SINCE_LAST_WEIGHT,
        Header: "Days Since Weighed",
        accessor: ({ lastWeight }): number | null =>
          lastWeight?.date ? Math.trunc(DateTime.local().diff(DateTime.fromISO(lastWeight.date), ["days"]).days) : null,
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.DAYS_SINCE_LAST_WEIGHT] || "\u2014",
        minWidth: 120,
        width: 120,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DLWG,
        Header: capitaliseFirstLetterOfEachWord(terms.avgDlwg),
        accessor: ({ growthRate }): string | null => growthRate?.toFixed(1) || null,
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.DLWG] || "\u2014",
        minWidth: 100,
        width: 100,
      },
      {
        id: COLUMN_IDS.SIRE,
        Header: "Sire",
        accessor: ({ linkToMySire, sirePassportNumber }): string | null =>
          linkToMySire?.passportNumber || sirePassportNumber || null,
        onCellClick,
        Cell: function SireCell({ row: { original } }): React.ReactNode {
          const sire = original?.linkToMySire;
          const isPedigree = sire?.pedigree;
          return sire ? (
            <AnimalPedigreeLabel
              id={`animal_sire_label__${original?.id}`}
              isPedigree={!!isPedigree}
              passportNumber={sire?.passportNumber}
            />
          ) : (
            original?.sirePassportNumber || "\u2014"
          );
        },
        minWidth: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DAM,
        Header: "Dam",
        accessor: ({ damPassportNumber }): string | null => damPassportNumber || null,
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.DAM] || "\u2014",
        minWidth: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.PREGNANCY_STATUS,
        Header: <TableHeaderCaptions title="Pregnancy" subtitle="Status" />,
        accessor: "isPregnant",
        onCellClick,
        Cell: function PregnancyStatusCell({ row: { original } }): React.ReactNode {
          return !original?.isMale && original?.isPregnant ? (
            <AnimalPregnancyLabel
              dueDate={original?.pregnancyDueDate}
              id={`animal_pregnancy_label__${original?.id}`}
              isMale={original?.isMale}
              isPregnant={!!original?.isPregnant}
            />
          ) : (
            "\u2014"
          );
        },
        minWidth: 120,
        width: 120,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.PREGNANCY_DUE_DATE,
        Header: <TableHeaderCaptions title="Pregnancy" subtitle="Due Date" />,
        accessor: "pregnancyDueDate",
        onCellClick,
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.PREGNANCY_DUE_DATE]
            ? getFormatDate(row.values[COLUMN_IDS.PREGNANCY_DUE_DATE])
            : "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.LAST_SYNCED,
        Header: "Last Synced",
        accessor: "lastRegulatorySyncedDate",
        onCellClick,
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.LAST_SYNCED] ? getFormatDate(row.values[COLUMN_IDS.LAST_SYNCED]) : "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.STATUS,
        Header: "Status",
        onCellClick,
        accessor: (animal): JSX.Element => {
          if (useEpds) {
            return renderPedigreeStatus(
              {
                pedigreeId: animal.pedigreeId,
                withdrawalEnd: animal.withdrawalEnd,
                deadAt: animal.deadAt,
                dateLeftFarm: animal.dateLeftFarm,
              },
              { isArchive, isCarcass: undefined, countryCode },
              getFormatDate,
            );
          }

          return renderStatus(
            {
              isRegulatorySynced: animal.isRegulatorySynced,
              withdrawalEnd: animal.withdrawalEnd,
              deadAt: animal.deadAt,
              dateLeftFarm: animal.dateLeftFarm,
            },
            { isArchive, isCarcass: undefined, countryCode },
            getFormatDate,
          );
        },
        disableSortBy: true,
        minWidth: isArchive ? 70 : 100,
        width: isArchive ? 70 : 100,
      },
      ...(showIndividualAnimalActionButtons
        ? [
            {
              ...getActionsColumn<AnimalFragment>(!!onPressEditAnimal),
              Cell: function ActionColumn({ row: { original } }): JSX.Element | null {
                const isOnFarm = !original?.dateLeftFarm && !original?.deadAt;
                if (!isOnFarm) {
                  return null;
                }
                if (onPressEditAnimal) {
                  return (
                    <button className="flex" onClick={() => onPressEditAnimal(original)}>
                      <Icon name="pencil" size="small" colour="blue" />
                      <p className="pl-2">Edit</p>
                    </button>
                  );
                }

                return (
                  <TableActionColumn
                    animal={original}
                    group={undefined}
                    isFarmWithGroups={numberOfGroups ? numberOfGroups > 0 : false}
                  />
                );
              },
              // sticky: "right",
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onCellClick,
      isArchive,
      tableOptions?.selectedRowsIds,
      setTableOptions,
      getFormatDate,
      weightUnits,
      numberOfGroups,
      showSelectColumn,
      showIndividualAnimalActionButtons,
      tableId,
    ],
  );

  const columnVisibilityRules = {
    [COLUMN_IDS.CURRENT_WEIGHT]: !isArchive,
    [COLUMN_IDS.DAYS_SINCE_LAST_WEIGHT]: !isArchive,
    [COLUMN_IDS.FIELD]: !isArchive,
    [COLUMN_IDS.PREGNANCY_DUE_DATE]: !isArchive,
    [COLUMN_IDS.PREGNANCY_STATUS]: !isArchive,
    [COLUMN_IDS.DEAD_AT]: isArchive,
    [COLUMN_IDS.DATE_LEFT_FARM]: isArchive,
    [COLUMN_IDS.KILL_WEIGHT]: isArchive,
    [COLUMN_IDS.KILL_QUALITY]: isArchive,
    [COLUMN_IDS.KILL_FAT_SCORE]: isArchive,
    [COLUMN_IDS.LAST_SYNCED]: countryCode.gb,
    [COLUMN_IDS.STATUS]: (!countryCode.gb && !isArchive) || countryCode.gb,
    [COLUMN_IDS.ANIMAL_ID]: activeIdPreferences?.passportNumber && !countryCode.us,
    [COLUMN_IDS.E_ID]: activeIdPreferences?.eId,
    [COLUMN_IDS.VISUAL_ID]: activeIdPreferences?.visualId,
    [COLUMN_IDS.BRUCELLOSIS_ID]: activeIdPreferences?.brucellosisId && countryCode.us,
    [COLUMN_IDS.HERDDOGG_ID]: activeIdPreferences?.herdDoggId && countryCode.us,
    [COLUMN_IDS.PEDIGREE_ID]: activeIdPreferences?.pedigreeId,
    [COLUMN_IDS.TATTOO_ID]: activeIdPreferences?.tattoo && countryCode.us,
    [COLUMN_IDS.TRICH_ID]: activeIdPreferences?.trichId && countryCode.us,
    [COLUMN_IDS.TSU_BARCODE]: activeIdPreferences?.tsuBarcode && countryCode.us,
    [COLUMN_IDS.NAME]: activeIdPreferences?.name && countryCode.us,
    [COLUMN_IDS.UHF_ID]: activeIdPreferences?.uhfId && countryCode.us,
    [COLUMN_IDS.BREEDR_ID]: activeIdPreferences?.breedrId && countryCode.us,
    [COLUMN_IDS.ALTERNATIVE_ID]: activeIdPreferences?.alternativeId && countryCode.us,
  };

  const hiddenColumns = Object.keys(columnVisibilityRules).filter((key) => {
    return !columnVisibilityRules[key];
  });

  const defaultColumnIds = countryCode.us
    ? isArchive
      ? Object.values(DEFAULT_COLUMN_IDS_US_ARCHIVE)
      : Object.values(DEFAULT_COLUMN_IDS_US)
    : Object.values(DEFAULT_COLUMN_IDS);

  const defaultColumns = createColumnObject(
    columns,
    currentLocalStorageColumns,
    hiddenColumns,
    defaultColumnIds,
    primaryIdCamelCase,
  );

  const [customColumns, setCustomColumns] = useState<Column<AnimalFragment>[]>(
    reorderColumns(columns, defaultColumns, primaryIdCamelCase),
  );

  const [customColumnTrigger, setCustomColumnTrigger] = useState<number>(0);
  useEffect(() => {
    setCustomColumns(
      currentLocalStorageColumns.length > 0
        ? reorderColumns(columns, currentLocalStorageColumns, primaryIdCamelCase)
        : reorderColumns(columns, defaultColumns, primaryIdCamelCase),
    );
  }, [columns, customColumnTrigger]);

  const getExportColumns = (): AnimalExportColumnInput[] => {
    const exportColumns = customColumns.map((column) => {
      return getAnimalExportColumn(column);
    });
    return removeNothings(exportColumns);
  };

  const handleAnimalsExport = async () => {
    const { data } = await exportCsv({
      variables: {
        input: {
          animalIds: selectedAnimalIds.length > 0 ? selectedAnimalIds : animalIds,
          businessUnit: businessUnitId.toString(),
          columns: getExportColumns(),
          dateHint: countryCode.us ? UploadCsvDateHintEnumType.Usa : UploadCsvDateHintEnumType.International,
          animalTypes: activeSpeciesTypeId ? [activeSpeciesTypeId] : undefined,
          orderBy: sortBy ? [sortBy] : undefined,
          isMale: getBooleanFilter(filters.sex, Sex.MALE, Sex.FEMALE),
          isPregnant: getBooleanFilter(filters.pregnancyStatus, PregnancyStatus.PREGNANT, PregnancyStatus.NOTPREGNANT),
          minDob: filters.dateOfBirth.from || undefined,
          maxDob: filters.dateOfBirth.to || undefined,
          minDof: filters.dateOnFarm.from || undefined,
          maxDof: filters.dateOnFarm.to || undefined,
          field: fieldId || filters.fieldId || undefined,
          group: groupId || filters.groupId || undefined,
          breed: filters.breed || undefined,
          animalTags: tagId ? [tagId] : filters.tagIds.length > 0 ? filters.tagIds : undefined,
        },
      },
    });
    dispatch(setMenuState("expanded"));
    dispatch(setTaskId(data?.animalsExportAsync?.export?.id));
  };
  const onChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);

  const debouncedSearchTerm = useMemo(() => {
    return debounce(onChangeSearchTerm, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearchTerm.cancel();
    };
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (isSelectedAnimalsView && !selectedAnimalIds.length) {
      setIsSelectedAnimalsView(false);
    }
  }, [selectedAnimalIds, isSelectedAnimalsView]);

  useEffect(() => {
    setPageNumber(1);
  }, [filters, setPageNumber]);

  useEffect(() => {
    return () => {
      if (resetSelectedAnimalsOnUnmount) {
        setTableOptions({ selectedRows: undefined, selectedRowsIds: undefined, id: tableId });
      }
    };
  }, [resetSelectedAnimalsOnUnmount, setTableOptions, tableId]);

  const caption = `${selectedAnimalIds?.length} Selected - View selection`;

  const animalsData = useMemo(
    () =>
      loading && animals.length === 0
        ? getLoadingStateRows<AnimalFragment>(pageSize, generatePlaceholderAnimalRow)
        : animals,
    [animals, loading, pageSize],
  );

  const [areFiltersActive, setAreFiltersActive] = useState(false);
  const initialFiltersRef = useRef(filters);

  useEffect(() => {
    if (JSON.stringify(initialFiltersRef.current) !== JSON.stringify(filters) || searchTerm !== "") {
      setAreFiltersActive(true);
    }
  }, [filters, searchTerm]);

  const emptyTableDisplay = () => {
    if (loading) {
      return <LoadingOverlay />;
    } else if (!areFiltersActive) {
      return <EmptyStatesMyCattleTab />;
    } else if (areFiltersActive && !loading) {
      return <EmptyStatesNoResults />;
    }
  };

  if (isSelectedAnimalsView) {
    return (
      <>
        <Flex item itemGutter>
          <Spacer baselineHeight={1} />

          {!!selectedAnimalIds.length && showAnimalActionButtons ? (
            <AnimalActions animalIds={selectedAnimalIds} />
          ) : null}
          <Spacer baselineHeight={1} />

          <Flex
            className={paginationStyles.table_pagination_target}
            container
            containerAlignItems="center"
            containerJustifyContent="flex-end"
            containerWrap="wrap"
          >
            <Button
              caption="Back to list"
              colour="grey"
              variant="hollow"
              onClick={() => setIsSelectedAnimalsView(false)}
            />
            <Flex item itemGrow />
            <TablePaginationTarget tableId={TABLE_IDS["ANIMAL_LIST_SERVER_SELECTED"]} />
            <div style={{ width: "10px" }} />
            <Flex>
              <ExportAnimalsButton
                isLoading={!!taskId}
                numAnimals={selectedRowIds?.size || 0}
                onClick={() => handleAnimalsExport()}
                isServerList
              />
            </Flex>
          </Flex>
        </Flex>

        <Spacer baselineHeight={1} />

        <Flex item itemGutter xs="fill">
          <SelectedAnimalsTable
            fromPage={fromPage}
            columns={customColumns}
            hiddenColumns={hiddenColumns}
            sourceTableId={tableId}
            rowStyles={rowStyles}
          />
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex item itemGutter>
        <Spacer baselineHeight={1} />

        {!!selectedAnimalIds.length && showAnimalActionButtons ? <AnimalActions animalIds={selectedAnimalIds} /> : null}

        <Spacer baselineHeight={1} />

        <Flex container containerJustifyContent="space-between">
          <Flex container>
            <Flex className={styles.search_input} item>
              <BaseInput
                type="text"
                onChange={debouncedSearchTerm}
                name="animal_search"
                placeholder={
                  (primaryId as unknown as IdentifierType) !== IdentifierType.EId &&
                  (secondaryId as unknown as IdentifierType) !== IdentifierType.EId
                    ? `Search by EID${primaryId ? `, ${identifierNameLookup(terms, primaryIdCamelCase)}` : ""}${
                        secondaryId ? ` or ${identifierNameLookup(terms, secondaryIdCamelCase)}` : ""
                      }`
                    : `Search by ${primaryId ? `${identifierNameLookup(terms, primaryIdCamelCase)}` : ""}${
                        secondaryId ? ` or ${identifierNameLookup(terms, secondaryIdCamelCase)}` : ""
                      }`
                }
              />
            </Flex>

            {showSelectColumn && Boolean(selectedAnimalIds.length) ? (
              <Flex item itemGutter>
                <Button caption={caption} onClick={() => setIsSelectedAnimalsView((state) => !state)} colour="green" />
              </Flex>
            ) : null}
          </Flex>

          <div className="flex justify-end flex-1 flex-wrap mb-2">
            <PaginationControl
              paginationProps={paginationProps}
              numTotalItems={totalNumberOfAnimals}
              ref={paginationProps.pageSizeMenuRef}
            />

            <TwButton
              title="Columns"
              className="ml-2"
              variant="outline"
              onClick={() => setColumnOptionsModalOpen(true)}
            >
              <span className="px-1">Columns</span>
              <Icon name="column" size="small" />
            </TwButton>
            <TwButton
              title="Filters"
              className="ml-2"
              onClick={() => dispatch(toggleFilter({ tableIdentifier: tableFilterIdentifier, showFilters }))}
              variant="outline"
            >
              <span className="px-1">Filters</span>
              <Icon name="filter" size="small" />
            </TwButton>
            <div className="ml-2">
              <ExportAnimalsButton
                isLoading={!!taskId}
                numAnimals={selectedRowIds?.size || totalNumberOfAnimals}
                onClick={() => handleAnimalsExport()}
                isServerList
              />
            </div>
          </div>
        </Flex>
      </Flex>
      {/* Filters block */}
      {showFilters ? (
        <Flex item itemGutter>
          <Flex className={cn(styles.filtersContainer)} container>
            {!excludedFilters?.tagId ? (
              <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
                <FieldSelect
                  label="Tags"
                  inputProps={{
                    name: "tags",
                    value: filters.tagIds,
                    multiple: true,
                    options: animalTagOptions,
                    placeholder: "No preference",
                    onChange: (event) => {
                      dispatch(
                        updateFilters({
                          tableIdentifier: tableFilterIdentifier,
                          filters: {
                            ...filters,
                            tagIds: event.target.value,
                          },
                        }),
                      );
                    },
                    isClearable: true,
                  }}
                />
              </Flex>
            ) : null}
            {!excludedFilters?.groupId ? (
              <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
                <FieldSelect
                  label="Group"
                  inputProps={{
                    name: "groups",
                    value: groups.find((group) => group.id === filters.groupId)?.name ?? "",
                    multiple: false,
                    options: groups,
                    placeholder: "No preference",
                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                      dispatch(
                        updateFilters({
                          tableIdentifier: tableFilterIdentifier,
                          filters: {
                            ...filters,
                            groupId: event.target.value,
                          },
                        }),
                      ),
                    labelKey: "name",
                    valueKey: "id",
                    isClearable: true,
                  }}
                />
              </Flex>
            ) : null}
            {!excludedFilters?.fieldId ? (
              <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
                <FieldSelect
                  label={"Location"}
                  inputProps={{
                    name: "fields",
                    value: fieldOptions.find((field) => field.value === filters.fieldId)?.value ?? "",
                    multiple: false,
                    options: fieldOptions,
                    placeholder: "No preference",
                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                      dispatch(
                        updateFilters({
                          tableIdentifier: tableFilterIdentifier,
                          filters: {
                            ...filters,
                            fieldId: event.target.value,
                          },
                        }),
                      ),
                    labelKey: "label",
                    valueKey: "key",
                    isClearable: true,
                  }}
                />
              </Flex>
            ) : null}
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Sex"
                inputProps={{
                  name: "sex",
                  value: filters.sex ?? "",
                  placeholder: "No preference",
                  options: [
                    {
                      key: 1,
                      value: Sex.MALE,
                      label: "Male",
                    },
                    {
                      key: 2,
                      value: Sex.FEMALE,
                      label: "Female",
                    },
                  ],
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                      updateFilters({
                        tableIdentifier: tableFilterIdentifier,
                        filters: {
                          ...filters,
                          sex: event.target.value,
                        },
                      }),
                    ),
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Pregnancy status"
                inputProps={{
                  name: "pregnancyStatus",
                  value: filters.pregnancyStatus ?? "",
                  placeholder: "No preference",
                  options: [
                    {
                      key: 1,
                      value: PregnancyStatus.PREGNANT,
                      label: "Pregnant",
                    },
                    {
                      key: 2,
                      value: PregnancyStatus.NOTPREGNANT,
                      label: "Not pregnant",
                    },
                  ],
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                      updateFilters({
                        tableIdentifier: tableFilterIdentifier,
                        filters: {
                          ...filters,
                          pregnancyStatus: event.target.value,
                        },
                      }),
                    ),
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Breed"
                inputProps={{
                  name: "breed",
                  value: filters.breed,
                  placeholder: "No preference",
                  options: breedOptions,
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                      updateFilters({
                        tableIdentifier: tableFilterIdentifier,
                        filters: {
                          ...filters,
                          breed: event.target.value,
                        },
                      }),
                    ),
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} m={6} xl={4}>
              <FieldDateRange
                label="DOB range"
                inputProps={{
                  from: {
                    value: filters.dateOfBirth.from ? new Date(filters.dateOfBirth.from) : undefined,
                    name: "dobDateFrom",
                    onChange: (date: Date) =>
                      dispatch(
                        updateFilters({
                          tableIdentifier: tableFilterIdentifier,
                          filters: {
                            ...filters,
                            dateOfBirth: {
                              ...filters.dateOfBirth,
                              from: date ? date.toISOString() : undefined,
                            },
                          },
                        }),
                      ),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                  to: {
                    value: filters.dateOfBirth.to ? new Date(filters.dateOfBirth.to) : undefined,
                    name: "dobDateTo",
                    onChange: (date: Date) =>
                      dispatch(
                        updateFilters({
                          tableIdentifier: tableFilterIdentifier,
                          filters: {
                            ...filters,
                            dateOfBirth: {
                              ...filters.dateOfBirth,
                              to: date ? date.toISOString() : undefined,
                            },
                          },
                        }),
                      ),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} m={6} xl={4}>
              <FieldDateRange
                label={`Date on ${terms.farm} range`}
                inputProps={{
                  from: {
                    value: filters.dateOnFarm.from ? new Date(filters.dateOnFarm.from) : undefined,
                    name: "dofDateFrom",
                    onChange: (date: Date) =>
                      dispatch(
                        updateFilters({
                          tableIdentifier: tableFilterIdentifier,
                          filters: {
                            ...filters,
                            dateOnFarm: {
                              ...filters.dateOnFarm,
                              from: date ? date.toISOString() : undefined,
                            },
                          },
                        }),
                      ),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                  to: {
                    value: filters.dateOnFarm.to ? new Date(filters.dateOnFarm.to) : undefined,
                    name: "dofDateTo",
                    onChange: (date: Date) =>
                      dispatch(
                        updateFilters({
                          tableIdentifier: tableFilterIdentifier,
                          filters: {
                            ...filters,
                            dateOnFarm: {
                              ...filters.dateOnFarm,
                              to: date ? date.toISOString() : undefined,
                            },
                          },
                        }),
                      ),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                }}
              />
            </Flex>
            <Flex item>
              <TwButton
                className="mt-8 ml-2"
                variant={"ghost"}
                onClick={() => {
                  setAreFiltersActive(false);
                  dispatch(resetFilters(tableFilterIdentifier));
                }}
              >
                X Clear Filters
              </TwButton>
            </Flex>
          </Flex>
        </Flex>
      ) : null}
      {/* End of filters block */}
      {animals.length === 0 ? (
        emptyTableDisplay()
      ) : (
        <Flex container item itemGrow>
          <Flex item itemGutter xs="fill">
            <Table<AnimalFragment>
              data={animalsData}
              isAnimals={!(fromPage === "field")}
              columns={customColumns}
              hiddenColumns={hiddenColumns}
              tableId={tableId}
              manualSortBy={true}
              manualPagination={true}
              rowStyles={rowStyles}
            />
          </Flex>
        </Flex>
      )}
      <ColumnOptionsModal
        columns={columns}
        hiddenColumns={hiddenColumns}
        isShowModal={columnOptionsModalOpen}
        onClose={() => setColumnOptionsModalOpen(false)}
        setCustomColumnnTrigger={setCustomColumnTrigger}
        defaultColumnIds={defaultColumnIds}
        tableId={tableId}
        primaryId={primaryIdCamelCase}
        secondaryId={secondaryIdCamelCase}
      />
    </>
  );
};

export const generatePlaceholderAnimalRow = () => ({
  id: "",
  tags: [],
  passportNumber: "—",
  isCastrated: false,
  isMale: false,
  animalBreeds: [],
  breedPercentages: [],
  updatedAt: "",
});
